// packages/web-ui/src/components/onboarding/OnboardingGate.tsx
//
// W2-S10 Task 1.6 (AC #1) — gate component that mounts the onboarding takeover
// only when the user has not yet completed onboarding (or has explicitly
// re-opened it via Settings).
//
// The host (`<App>` or any parent) wraps its children in `<OnboardingGate>`.
// The gate consults the slice's `selectShouldShowTakeover()` and either:
//   - Renders the takeover overlaying its children, or
//   - Renders just the children when onboarding is complete.

import type { JSX, ReactNode } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import { OnboardingTakeover, type OnboardingTakeoverProps } from './OnboardingTakeover.js';
import {
  selectShouldShowTakeover,
  type OnboardingSlice,
} from '../../state/onboarding.js';

export interface OnboardingGateProps {
  /** Vanilla zustand store providing the onboarding slice. */
  store: StoreApi<OnboardingSlice>;
  /** Children rendered behind the takeover (always present in the DOM). */
  children: ReactNode;
  /** Optional folder picker forwarded to Step1Workspace. */
  pickFolder?: OnboardingTakeoverProps['pickFolder'];
  /** Optional connection tester forwarded to Step2Provider. */
  testConnection?: OnboardingTakeoverProps['testConnection'];
  /** Optional test-id override. */
  testId?: string;
}

/**
 * Build-time switch — when `VITE_TESSA_SKIP_ONBOARDING=true` is set during
 * `vite build`, the onboarding takeover is hard-disabled for every user.
 * Used by the cloud demo deployment, where workspace path is fixed at
 * `/workspace`, LLM credentials are wired via server-side env vars from
 * Key Vault, and per-user wizard steps would just collect redundant input.
 * Local dev + extension builds leave it undefined → wizard behaves normally.
 */
const SKIP_ONBOARDING_AT_BUILD_TIME =
  import.meta.env.VITE_TESSA_SKIP_ONBOARDING === 'true';

export function OnboardingGate({
  store,
  children,
  pickFolder,
  testConnection,
  testId = 'onboarding-gate',
}: OnboardingGateProps): JSX.Element {
  const state = useStore(store);
  const showTakeover =
    !SKIP_ONBOARDING_AT_BUILD_TIME && selectShouldShowTakeover(state);

  return (
    <div data-testid={testId}>
      {children}
      {showTakeover ? (
        <OnboardingTakeover
          store={store}
          pickFolder={pickFolder}
          testConnection={testConnection}
        />
      ) : null}
    </div>
  );
}
